import React, { Component } from 'react';
import { Row, Col, Card, Radio, DatePicker, Table, Spin, Empty } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import moment from 'moment';

const { RangePicker } = DatePicker;

const COLORS = ['#53826A', '#344E41', '#FF8A00', '#4096ff', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#faad14', '#52c41a', '#1890ff', '#2f54eb'];

const LABEL_MAP = {
	email: 'Email',
	whatsapp: 'WhatsApp',
	web_upload: 'Web Upload',
	mobile: 'Mobile',
	alipay: 'Alipay',
	apple_pay: 'Apple Pay',
	bank_transfer: 'Bank Transfer',
	cash: 'Cash',
	cheque: 'Cheque',
	credit_debit_card: 'Credit/Debit Card',
	google_pay: 'Google Pay',
	octopus: 'Octopus',
	other: 'Other',
	paypal: 'PayPal',
	wechat_pay: 'WeChat Pay',
	advertising_and_marketing: 'Advertising & Marketing',
	bank_and_finance_charges: 'Bank & Finance Charges',
	benefits: 'Benefits',
	car: 'Car',
	communication: 'Communication',
	depreciation: 'Depreciation',
	donations_and_charity: 'Donations & Charity',
	education_and_training: 'Education & Training',
	equipment: 'Equipment',
	fees: 'Fees',
	home_office: 'Home Office',
	insurance: 'Insurance',
	interest: 'Interest',
	labor: 'Labor',
	maintenance: 'Maintenance',
	materials: 'Materials',
	meals_and_entertainment: 'Meals & Entertainment',
	medical: 'Medical',
	office_supplies: 'Office Supplies',
	parking_and_tolls: 'Parking & Tolls',
	professional_services: 'Professional Services',
	rent: 'Rent',
	shipping_and_delivery: 'Shipping & Delivery',
	software_and_subscriptions: 'Software & Subscriptions',
	taxes: 'Taxes',
	travel: 'Travel',
	utilities: 'Utilities'
};

const formatLabel = (slug) => LABEL_MAP[slug] || slug;

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const formatXAxisTick = (date) => {
	if (!date) return '';
	if (date.length === 4) return date; // yearly: "2026"
	if (date.length === 7) { // monthly: "2026-04" → "Apr '26"
		const m = parseInt(date.slice(5), 10);
		return `${MONTH_SHORT[m - 1]} '${date.slice(2, 4)}`;
	}
	return date.slice(5).replace('-', '/'); // daily/weekly: "2026-04-07" → "04/07"
};

const formatCurrency = (value) => {
	if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
	if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
	return value.toFixed(2);
};

class DashboardPage extends Component {
	constructor(props) {
		super(props);
		this.state = {
			period: 'monthly',
			customRange: null
		};
	}

	componentDidMount() {
		this.fetchAllData();
	}

	componentWillUnmount() {
		this.props.resetPage();
	}

	fetchAllData = () => {
		const query = this.buildQuery();
		this.props.getSummary(query);
		this.props.getOvertime(query);
		this.props.getByChannel(query);
		this.props.getByCategory(query);
		this.props.getByPaymentMethod(query);
	}

	buildQuery = () => {
		const { period, customRange } = this.state;
		if (customRange && customRange.length === 2) {
			return {
				start_date: customRange[0].startOf('day').toISOString(),
				end_date: customRange[1].endOf('day').toISOString()
			};
		}
		return { period };
	}

	handlePeriodChange = (e) => {
		this.setState({ period: e.target.value, customRange: null }, this.fetchAllData);
	}

	handleRangeChange = (dates) => {
		if (dates && dates.length === 2) {
			this.setState({ customRange: dates, period: null }, this.fetchAllData);
		} else {
			this.setState({ customRange: null, period: 'monthly' }, this.fetchAllData);
		}
	}

	renderSummaryTable = () => {
		const { summary, loadingSummary } = this.props.dashboardPageProps;

		if (loadingSummary) return <Spin />;
		if (!summary) return <Empty description="No data" />;

		const { current_period, previous_period } = summary;
		const currentMap = {};
		(current_period.by_currency || []).forEach(item => {
			currentMap[item.currency] = item;
		});
		const previousMap = {};
		(previous_period.by_currency || []).forEach(item => {
			previousMap[item.currency] = item;
		});

		const currencies = [...new Set([
			...Object.keys(currentMap),
			...Object.keys(previousMap)
		])];

		if (currencies.length === 0) return <Empty description="No expenses found" />;

		const dataSource = currencies.map(currency => {
			const current = currentMap[currency] || { total: 0, count: 0 };
			const previous = previousMap[currency] || { total: 0, count: 0 };
			const change = previous.total > 0
				? ((current.total - previous.total) / previous.total * 100).toFixed(1)
				: current.total > 0 ? '100.0' : '0.0';

			return {
				key: currency,
				currency: currency.toUpperCase(),
				currentTotal: current.total,
				currentCount: current.count,
				previousTotal: previous.total,
				previousCount: previous.count,
				change
			};
		});

		const columns = [
			{ title: 'Currency', dataIndex: 'currency', key: 'currency' },
			{
				title: 'Current Period',
				children: [
					{ title: 'Total', dataIndex: 'currentTotal', key: 'currentTotal', render: v => v.toFixed(2), align: 'right' },
					{ title: 'Count', dataIndex: 'currentCount', key: 'currentCount', align: 'right' }
				]
			},
			{
				title: 'Previous Period',
				children: [
					{ title: 'Total', dataIndex: 'previousTotal', key: 'previousTotal', render: v => v.toFixed(2), align: 'right' },
					{ title: 'Count', dataIndex: 'previousCount', key: 'previousCount', align: 'right' }
				]
			},
			{
				title: 'Change %',
				dataIndex: 'change',
				key: 'change',
				align: 'right',
				render: v => {
					const num = parseFloat(v);
					const color = num > 0 ? '#f5222d' : num < 0 ? '#52c41a' : '#000';
					return <span style={{ color }}>{num > 0 ? '+' : ''}{v}%</span>;
				}
			}
		];

		return (
			<Table
				columns={columns}
				dataSource={dataSource}
				pagination={false}
				size="small"
				bordered
			/>
		);
	}

	renderOvertimeChart = () => {
		const { overtime, loadingOvertime } = this.props.dashboardPageProps;

		if (loadingOvertime) return <Spin />;
		if (!overtime || !overtime.data || overtime.data.length === 0) return <Empty description="No data" />;

		// Collect all currencies
		const currencies = new Set();
		overtime.data.forEach(item => {
			(item.by_currency || []).forEach(c => currencies.add(c.currency));
		});

		// Build chart data with one key per currency
		const chartData = overtime.data.map(item => {
			const point = { date: item.date };
			(item.by_currency || []).forEach(c => {
				point[c.currency.toUpperCase()] = c.total;
			});
			return point;
		});

		const currencyArray = [...currencies];

		return (
			<ResponsiveContainer width="100%" height={300}>
				<LineChart data={chartData}>
					<CartesianGrid strokeDasharray="3 3" />
					<XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={formatXAxisTick} angle={-30} textAnchor="end" height={50} />
					<YAxis tickFormatter={formatCurrency} />
					<Tooltip formatter={(value) => value.toFixed(2)} />
					<Legend />
					{currencyArray.map((currency, index) => (
						<Line
							key={currency}
							type="monotone"
							dataKey={currency.toUpperCase()}
							stroke={COLORS[index % COLORS.length]}
							strokeWidth={2}
							dot={{ r: 3 }}
						/>
					))}
				</LineChart>
			</ResponsiveContainer>
		);
	}

	renderPieChart = (data, loading, nameKey) => {
		if (loading) return <Spin />;
		if (!data || !data.data || data.data.length === 0) return <Empty description="No data" />;

		// Sum totals across currencies for the pie chart
		const chartData = data.data.map(item => {
			const total = (item.by_currency || []).reduce((sum, c) => sum + c.total, 0);
			return {
				name: formatLabel(item[nameKey]),
				value: total,
				count: (item.by_currency || []).reduce((sum, c) => sum + c.count, 0)
			};
		}).sort((a, b) => b.value - a.value);

		const renderCustomLabel = ({ name, percent }) => {
			if (percent < 0.05) return null;
			return `${name} (${(percent * 100).toFixed(0)}%)`;
		};

		return (
			<ResponsiveContainer width="100%" height={300}>
				<PieChart>
					<Pie
						data={chartData}
						cx="50%"
						cy="50%"
						outerRadius={100}
						dataKey="value"
						label={renderCustomLabel}
						labelLine={true}
					>
						{chartData.map((entry, index) => (
							<Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
						))}
					</Pie>
					<Tooltip
						formatter={(value, name) => [value.toFixed(2), name]}
					/>
					<Legend />
				</PieChart>
			</ResponsiveContainer>
		);
	}

	render() {
		const { period, customRange } = this.state;
		const { byChannel, byCategory, byPaymentMethod, loadingByChannel, loadingByCategory, loadingByPaymentMethod } = this.props.dashboardPageProps;

		return (
			<div style={{ padding: '24px' }}>
				<h2 style={{ marginBottom: 16 }}>Dashboard</h2>

				<Card style={{ marginBottom: 24 }}>
					<div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
						<Radio.Group
							value={period}
							onChange={this.handlePeriodChange}
							buttonStyle="solid"
						>
							<Radio.Button value="daily">Daily</Radio.Button>
							<Radio.Button value="weekly">Weekly</Radio.Button>
							<Radio.Button value="monthly">Monthly</Radio.Button>
							<Radio.Button value="yearly">Yearly</Radio.Button>
						</Radio.Group>
						<RangePicker
							value={customRange}
							onChange={this.handleRangeChange}
							allowClear
							placeholder={['Start Date', 'End Date']}
						/>
					</div>
				</Card>

				<Card title="Expense Summary" style={{ marginBottom: 24 }}>
					{this.renderSummaryTable()}
				</Card>

				<Card title="Expenses Over Time" style={{ marginBottom: 24 }}>
					{this.renderOvertimeChart()}
				</Card>

				<Row gutter={[24, 24]}>
					<Col xs={24} lg={8}>
						<Card title="By Channel">
							{this.renderPieChart(byChannel, loadingByChannel, 'channel')}
						</Card>
					</Col>
					<Col xs={24} lg={8}>
						<Card title="By Category">
							{this.renderPieChart(byCategory, loadingByCategory, 'category')}
						</Card>
					</Col>
					<Col xs={24} lg={8}>
						<Card title="By Payment Method">
							{this.renderPieChart(byPaymentMethod, loadingByPaymentMethod, 'payment_method')}
						</Card>
					</Col>
				</Row>
			</div>
		);
	}
}

export default DashboardPage;
